import { addHours, differenceInHours, isAfter, isBefore, startOfHour } from 'date-fns'

/**
 * Get hours and minutes in a specific timezone using Intl API
 * @param {Date} date - The date to convert
 * @param {string} timezone - The timezone (e.g., 'Europe/Berlin')
 * @returns {{ hour: number, minute: number }}
 */
function getTimeInTimezone(date, timezone) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    })
    const parts = formatter.formatToParts(date)
    const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10)
    const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10)
    return { hour, minute }
  } catch {
    // Fallback to local time if timezone is invalid
    return { hour: date.getHours(), minute: date.getMinutes() }
  }
}

/**
 * Checks if a given time falls within any quiet hour period
 *
 * @param {Date} date - The date/time to check
 * @param {Array} quietHours - Array of quiet hour objects with start_time, end_time, enabled
 * @param {string} timezone - Account timezone (e.g., 'Europe/Berlin')
 * @returns {Object} { isInQuietHours: boolean, quietHourInfo: object }
 */
export function isTimeInQuietHours(date, quietHours, timezone = 'UTC') {
  if (!date || !quietHours || quietHours.length === 0) {
    return { isInQuietHours: false }
  }

  // Convert to account timezone using Intl API
  const { hour, minute } = getTimeInTimezone(date, timezone)
  const timeInMinutes = hour * 60 + minute

  for (const qh of quietHours) {
    // Skip disabled quiet hours
    if (qh.enabled === false) continue

    const [startHour, startMin] = (qh.start_time || '22:00').split(':').map(Number)
    const [endHour, endMin] = (qh.end_time || '08:00').split(':').map(Number)

    const startMinutes = startHour * 60 + startMin
    const endMinutes = endHour * 60 + endMin

    // Handle overnight quiet hours (e.g., 22:00 - 08:00)
    if (startMinutes > endMinutes) {
      // Overnight: quiet if time >= start OR time < end
      if (timeInMinutes >= startMinutes || timeInMinutes < endMinutes) {
        return {
          isInQuietHours: true,
          quietHourInfo: qh
        }
      }
    } else {
      // Same day: quiet if start <= time < end
      if (timeInMinutes >= startMinutes && timeInMinutes < endMinutes) {
        return {
          isInQuietHours: true,
          quietHourInfo: qh
        }
      }
    }
  }

  return { isInQuietHours: false }
}

/**
 * Validates if a proposed schedule time respects the post interval
 *
 * @param {Date} proposedDate - The date/time being proposed for scheduling
 * @param {Array} existingStories - Array of existing scheduled stories
 * @param {number} postIntervalHours - Minimum hours between posts (from brand settings)
 * @param {string} currentStoryId - ID of story being rescheduled (to exclude from checks)
 * @returns {Object} { isValid: boolean, reason: string, conflictingStory: object }
 */
export function validateScheduleTime(proposedDate, existingStories, postIntervalHours, currentStoryId = null) {
  if (!proposedDate || !existingStories || !postIntervalHours) {
    return { isValid: true }
  }

  const minIntervalHours = postIntervalHours || 24

  // Filter out the current story being rescheduled
  const otherStories = existingStories.filter(story => {
    const storyId = story.story_id || story.id
    return storyId !== currentStoryId
  })

  // Check each story
  for (const story of otherStories) {
    const scheduledFor = story.scheduled_for || story.scheduledFor || story.scheduled_at
    if (!scheduledFor) continue

    const storyDate = new Date(scheduledFor)
    const hoursDiff = Math.abs(differenceInHours(proposedDate, storyDate))

    if (hoursDiff < minIntervalHours) {
      const isBefore = proposedDate < storyDate
      return {
        isValid: false,
        reasonKey: 'errors:schedule.tooCloseToStory',
        reasonParams: { hours: hoursDiff.toFixed(1), minHours: minIntervalHours },
        conflictingStory: story,
        isBefore,
        hoursDiff
      }
    }
  }

  return { isValid: true }
}

/**
 * Calculates the next available time slots respecting the post interval and quiet hours
 *
 * @param {Array} existingStories - Array of existing scheduled stories
 * @param {number} postIntervalHours - Minimum hours between posts
 * @param {number} maxSlots - Maximum number of slots to generate (default: 168 = 1 week)
 * @param {Array} quietHours - Array of quiet hour objects (optional)
 * @param {string} timezone - Account timezone (optional)
 * @returns {Array<Date>} Array of available time slots
 */
export function calculateAvailableTimeSlots(existingStories, postIntervalHours, maxSlots = 168, quietHours = null, timezone = 'UTC') {
  const minIntervalHours = postIntervalHours || 24
  const now = new Date()
  const startTime = startOfHour(addHours(now, 1)) // Start from next hour

  const availableSlots = []

  // Generate potential slots (every hour for the next week)
  for (let i = 0; i < maxSlots; i++) {
    const candidateSlot = addHours(startTime, i)

    // Check quiet hours first
    if (quietHours && quietHours.length > 0) {
      const quietCheck = isTimeInQuietHours(candidateSlot, quietHours, timezone)
      if (quietCheck.isInQuietHours) {
        continue
      }
    }

    // Check if this slot conflicts with any existing story
    const validation = validateScheduleTime(candidateSlot, existingStories, minIntervalHours)

    if (validation.isValid) {
      availableSlots.push(candidateSlot)
    }
  }

  return availableSlots
}

/**
 * Finds the next available time slot after a given date
 *
 * @param {Date} afterDate - Find slot after this date
 * @param {Array} existingStories - Array of existing scheduled stories
 * @param {number} postIntervalHours - Minimum hours between posts
 * @param {string} currentStoryId - ID of story being rescheduled (to exclude)
 * @returns {Date} Next available time slot
 */
export function findNextAvailableSlot(afterDate, existingStories, postIntervalHours, currentStoryId = null) {
  const minIntervalHours = postIntervalHours || 24
  let candidateDate = startOfHour(afterDate || new Date())

  // Keep incrementing by 1 hour until we find a valid slot
  let maxAttempts = 168 * 2 // 2 weeks worth of hours
  let attempts = 0

  while (attempts < maxAttempts) {
    candidateDate = addHours(candidateDate, 1)

    const validation = validateScheduleTime(candidateDate, existingStories, minIntervalHours, currentStoryId)

    if (validation.isValid) {
      return candidateDate
    }

    attempts++
  }

  // Fallback: just add the interval to the last story
  const lastStory = existingStories
    .filter(s => s.scheduled_for || s.scheduledFor)
    .sort((a, b) => {
      const aDate = new Date(a.scheduled_for || a.scheduledFor)
      const bDate = new Date(b.scheduled_for || b.scheduledFor)
      return bDate - aDate
    })[0]

  if (lastStory) {
    const lastDate = new Date(lastStory.scheduled_for || lastStory.scheduledFor)
    return addHours(lastDate, minIntervalHours)
  }

  return addHours(new Date(), minIntervalHours)
}

/**
 * Custom filter function for react-datepicker to only allow valid time slots
 *
 * @param {Date} date - Date to check
 * @param {Array} existingStories - Array of existing scheduled stories
 * @param {number} postIntervalHours - Minimum hours between posts
 * @param {string} currentStoryId - ID of story being rescheduled
 * @param {Array} quietHours - Array of quiet hour objects (optional)
 * @param {string} timezone - Account timezone (optional)
 * @returns {boolean} True if date is a valid time slot
 */
export function filterAvailableTime(date, existingStories, postIntervalHours, currentStoryId = null, quietHours = null, timezone = 'UTC') {
  if (!date) return false

  // Must be in the future
  if (isBefore(date, new Date())) {
    return false
  }

  // Check quiet hours
  if (quietHours && quietHours.length > 0) {
    const quietCheck = isTimeInQuietHours(date, quietHours, timezone)
    if (quietCheck.isInQuietHours) {
      return false
    }
  }

  const validation = validateScheduleTime(date, existingStories, postIntervalHours, currentStoryId)
  return validation.isValid
}

/**
 * Get suggested time slots (next N available slots)
 *
 * @param {Array} existingStories - Array of existing scheduled stories
 * @param {number} postIntervalHours - Minimum hours between posts
 * @param {number} count - Number of suggestions (default: 5)
 * @param {Array} quietHours - Array of quiet hour objects (optional)
 * @param {string} timezone - Account timezone (optional)
 * @returns {Array<{date: Date, label: string}>} Suggested time slots with labels
 */
export function getSuggestedTimeSlots(existingStories, postIntervalHours, count = 5, quietHours = null, timezone = 'UTC') {
  const slots = calculateAvailableTimeSlots(existingStories, postIntervalHours, 168, quietHours, timezone)

  return slots.slice(0, count).map(slot => {
    const hoursFromNow = differenceInHours(slot, new Date())

    // Return labelKey and params for i18n translation
    const labelKey = hoursFromNow < 24
      ? 'dashboard:upcomingSchedule.timeSlot.inHours'
      : 'dashboard:upcomingSchedule.timeSlot.inDays'
    const labelParams = hoursFromNow < 24
      ? { hours: hoursFromNow }
      : { days: Math.floor(hoursFromNow / 24), hours: hoursFromNow % 24 }

    return { date: slot, labelKey, labelParams }
  })
}

/**
 * Calculates cascading reschedules when inserting a story "between" others
 *
 * @param {Date} proposedDate - The date/time being proposed
 * @param {string} currentStoryId - ID of story being rescheduled
 * @param {Array} existingStories - Array of existing scheduled stories
 * @param {number} postIntervalHours - Minimum hours between posts
 * @returns {Object} { needsCascade: boolean, affectedStories: Array, newSchedules: Array }
 */
export function calculateCascadingReschedules(proposedDate, currentStoryId, existingStories, postIntervalHours) {
  const minIntervalHours = postIntervalHours || 24

  // Filter and sort stories (excluding current story being rescheduled)
  const otherStories = existingStories
    .filter(story => {
      const storyId = story.story_id || story.id
      return storyId !== currentStoryId
    })
    .map(story => ({
      ...story,
      scheduledDate: new Date(story.scheduled_for || story.scheduledFor || story.scheduled_at)
    }))
    .sort((a, b) => a.scheduledDate - b.scheduledDate)

  // Find stories that come after the proposed date
  const storiesAfter = otherStories.filter(story => isAfter(story.scheduledDate, proposedDate))

  if (storiesAfter.length === 0) {
    return { needsCascade: false, affectedStories: [], newSchedules: [] }
  }

  // Check if any story after violates the interval
  const affectedStories = []
  const newSchedules = []
  let lastDate = proposedDate

  for (const story of storiesAfter) {
    const hoursDiff = differenceInHours(story.scheduledDate, lastDate)

    if (hoursDiff < minIntervalHours) {
      // This story needs to be moved
      const newDate = addHours(lastDate, minIntervalHours)
      affectedStories.push(story)
      newSchedules.push({
        storyId: story.story_id || story.id,
        oldDate: story.scheduledDate,
        newDate: newDate,
        hoursShifted: differenceInHours(newDate, story.scheduledDate)
      })
      lastDate = newDate
    } else {
      // This story is far enough, no need to move it or any after it
      lastDate = story.scheduledDate
    }
  }

  return {
    needsCascade: affectedStories.length > 0,
    affectedStories,
    newSchedules
  }
}

/**
 * Checks if proposed date is valid OR can be made valid with cascading
 *
 * @param {Date} proposedDate - The date/time being proposed
 * @param {string} currentStoryId - ID of story being rescheduled
 * @param {Array} existingStories - Array of existing scheduled stories
 * @param {number} postIntervalHours - Minimum hours between posts
 * @returns {Object} { isValid: boolean, reason: string, canCascade: boolean, cascadeInfo: object }
 */
export function validateWithCascadeOption(proposedDate, currentStoryId, existingStories, postIntervalHours) {
  const minIntervalHours = postIntervalHours || 24

  // First check if it's valid without cascading
  const basicValidation = validateScheduleTime(proposedDate, existingStories, minIntervalHours, currentStoryId)

  if (basicValidation.isValid) {
    return {
      isValid: true,
      canCascade: false
    }
  }

  // Check if the conflict is with a story BEFORE the proposed date
  const conflictingStory = basicValidation.conflictingStory
  if (conflictingStory && basicValidation.isBefore) {
    // Conflict with a story before - can't cascade backwards
    return {
      isValid: false,
      reasonKey: basicValidation.reasonKey,
      reasonParams: basicValidation.reasonParams,
      canCascade: false
    }
  }

  // Check if we can cascade
  const cascadeInfo = calculateCascadingReschedules(proposedDate, currentStoryId, existingStories, minIntervalHours)

  if (cascadeInfo.needsCascade) {
    return {
      isValid: false,
      reasonKey: 'errors:schedule.wouldCascade',
      reasonParams: { count: cascadeInfo.affectedStories.length },
      canCascade: true,
      cascadeInfo
    }
  }

  // Shouldn't reach here, but fallback
  return {
    isValid: false,
    reasonKey: basicValidation.reasonKey,
    reasonParams: basicValidation.reasonParams,
    canCascade: false
  }
}
